/* eslint-disable react/prop-types */
import React from 'react'
import ReactList from 'react-list'
import styled, { css } from 'styled-components'
import {
  Modal as BaseModal,
  ModalHeader,
  ModalBody as BaseModalBody,
  ModalFooter as BaseModalFooter,
  Label,
  Input,
} from 'reactstrap'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import shortid from 'shortid'

import moment from '../../../lib/moment'
import Button from '../../../lib/components/Button'
import SearchField from '../../../lib/components/SearchField'
import LoadingBar from '../../../lib/components/LoadingBar'
import Tooltip, { withTooltip } from '../../../lib/components/Tooltip'
import { createTable } from '../../../lib/components/Table'
import BaseTypography from '../../../lib/typography'

import EmptyStateImage from '../../../static/images/document-add-black.svg'
import TrashIcon from '../../../static/images/trash-default.svg'

import bindActionCreators from '../../../lib/utils/bindActionCreators'

import { ppsAction } from '../../../actions'
import { ppsSelector } from '../../../selectors'
import { financialFormatter } from '../../../lib/utils'

const Modal = styled(BaseModal)`
  .modal-content {
    height: 80vh;
    position: relative;
  }

  input[type='file'] {
    display: none;
  }

  @media (min-width: 0) {
    max-width: 90%;
  }
`

const ModalBody = styled(BaseModalBody)`
  height: calc(100% - 57px);
  padding: 16px 16px 0;
  position: relative;
`

const EmptyState = {
  Container: styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
  Image: styled.img`
    width: 170px;
    height: 224px;
    transform: scale(0.85);
  `,
  Text: styled.div`
    color: #a1a1a1;
    font-family: 'Gotham';
    font-size: 16px;
    line-height: 22px;
  `,
}

const Trash = styled.i`
  width: 20px;
  height: 20px;

  background-image: url(${TrashIcon});
  background-repeat: no-repeat;
  background-position: center;

  cursor: pointer;
`

const Typography = {
  ...BaseTypography,
  ErrorLink: styled(BaseTypography.Link)`
    margin-left: 16px;
    color: #d44b49;
    font-size: 13px;
    line-height: 16px;
    cursor: pointer;
  `,
}

// prettier-ignore
const BaseTable = createTable(css`
  grid-template-columns:
    100px
    100px
    300px
    180px
    90px
    90px
    100px
    100px
    minmax(20%, 1fr)
    110px;
`)

const Table = {
  ...BaseTable,
  RowCell: styled(BaseTable.RowCell)`
    padding: 0 12px;
  `,
  RowCellTooltip: styled(BaseTable.RowCell)`
    ${withTooltip}

    ${Tooltip} {
      top: 80%;
    }

    &:hover ${Tooltip} {
      transition-delay: 1s;
    }
  `,
}

const Layout = {
  Actions: styled.div`
    display: flex;
    margin-bottom: 8px;

    > :not(:last-child) {
      margin-right: 16px;
    }

    > :first-child {
      margin-right: auto;
    }
  `,
  FileName: styled.div`
    display: grid;
    align-items: center;
    column-gap: 16px;
    grid-auto-flow: column;
  `,
  ScrollWrapper: styled.div`
    height: calc(100% - 112px);
    padding-bottom: 16px;

    overflow-x: auto;
    overflow-y: scroll;
  `,
  Truncate: styled.div`
    max-height: 100%;
    overflow: hidden;

    // ! non-standard
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  `,
}

/**
 * @typedef {object} Props
 * @property {Array} importData
 * @property {Array} transactions
 * @property {typeof ppsAction.importActualCost} onFileUpload
 * @property {typeof ppsAction.resetImportActual} clear
 * @property {typeof ppsAction.fetchProjectsData} refresh
 * @property {boolean} isFetching
 * @property {boolean} canImport
 * @property {object} status
 */

/**
 * @typedef {object} State
 * @property {{ root: boolean, fail: boolean, confirm: boolean }} modal_open
 * @property {File} file
 * @property {string} remark
 */

/** @extends {React.Component<Props, State>} */
class AccountingMenu extends React.Component {
  state = {
    modal_open: {
      root: false,
      fail: false,
      confirm: false,
    },
    file: null,
    remark: '',
  }

  setStateAsync = newState => new Promise(res => this.setState(newState, res))

  /** * @param {React.ChangeEvent<HTMLInputElement>} e */
  setRemark = e => this.setState({ remark: e.target.value })

  toggleModal = target => () => {
    this.setState(state => ({
      modal_open: {
        ...state.modal_open,
        [target]: !state.modal_open[target],
      },
    }))
  }

  toggleRootModal = () => {
    this.toggleModal('root')()
    this.clearUploadData()
  }

  toggleConfirmModal = () => {
    this.toggleModal('confirm')()
    this.setState({ remark: '' })
  }

  fileInputRef = React.createRef()

  clearUploadData = () => {
    this.setState({ file: null })
    this.props.clear()
  }

  openFileInput = () => {
    this.fileInputRef.current.click()
  }

  /** @param {React.ChangeEvent<HTMLInputElement>} e */
  handleFileCheck = async e => {
    const access_token = localStorage.getItem('access_token')
    const file = e.target.files[0]
    const data = new FormData()

    // * monkey patching for handling file which isn't 'text/csv' type
    if (!(file.type === 'text/csv' || file.name.endsWith('.csv'))) return

    data.set('csv', file)
    data.set('is_submit', JSON.stringify(false))

    await this.props.onFileUpload(data, access_token)
    await this.setStateAsync({ file })
  }

  handleImportCost = async () => {
    const access_token = localStorage.getItem('access_token')
    const { file, remark } = this.state
    const data = new FormData()

    data.set('csv', file)
    data.set('remark', remark)
    data.set('is_submit', JSON.stringify(true))

    this.toggleModal('root')()
    this.toggleModal('confirm')()
    this.setState({ remark: '' })

    await this.props.onFileUpload(data, access_token)
    await this.props.refresh(access_token)

    this.clearUploadData()
  }

  renderItem = (idx, key) => {
    const { transactions } = this.props
    return (
      <Table.Row key={ key }>
        <Table.RowCellTooltip>
          <Table.Ellipsis>{transactions[idx].project_code}</Table.Ellipsis>
          <Tooltip>{transactions[idx].project_code}</Tooltip>
        </Table.RowCellTooltip>
        <Table.RowCell>{transactions[idx].sub_project_code}</Table.RowCell>
        <Table.RowCell>{transactions[idx].project_name}</Table.RowCell>
        <Table.RowCell>{transactions[idx].client_name}</Table.RowCell>
        <Table.RowCell>{transactions[idx].account_code}</Table.RowCell>
        <Table.RowCell>{transactions[idx].type}</Table.RowCell>
        <Table.RowCell>{transactions[idx].product_name}</Table.RowCell>
        <Table.RowCell>{moment(transactions[idx].date).format('DD/MM/YYYY')}</Table.RowCell>
        <Table.RowCell>
          <Layout.Truncate>{transactions[idx].description}</Layout.Truncate>
        </Table.RowCell>
        <Table.RowCell currency>{financialFormatter(transactions[idx].amount)}</Table.RowCell>
      </Table.Row>
    )
  }

  render = () => {
    const { modal_open, file } = this.state
    const { status, isFetching, transactions, search, handleSearch, canImport } = this.props
    const confirmable = file && status.success_count > 0

    return (
      <React.Fragment>
        <SearchField
          placeholder="Search by name..."
          style={ { marginLeft: 'auto' } }
          onChange={ e => e.target.value === '' && handleSearch(e.target.value) }
          onKeyDown={ e => e.keyCode === 13 && handleSearch(e.target.value) }
          defaultValue={ search }
        />
        {canImport && (
          <Button color="info" onClick={ this.toggleRootModal }>
            Import Cost
          </Button>
        )}
        <Modal isOpen={ modal_open.root } toggle={ this.toggleRootModal } size="lg" backdrop="static">
          <LoadingBar isFetching={ isFetching } />
          <ModalHeader>
            <Typography.ModalHeader>Import Actual Cost</Typography.ModalHeader>
          </ModalHeader>
          <ModalBody>
            {!file && (
              <EmptyState.Container>
                <EmptyState.Image src={ EmptyStateImage } />
                <EmptyState.Text>Select files to Upload</EmptyState.Text>
              </EmptyState.Container>
            )}
            <Layout.Actions>
              {!file ? (
                <Button color="info" onClick={ this.openFileInput }>
                  ADD FILE
                  <input
                    type="file"
                    accept="text/csv,.csv"
                    ref={ this.fileInputRef }
                    onChange={ this.handleFileCheck }
                  />
                </Button>
              ) : (
                <Layout.FileName>
                  <Typography.Link>{file.name}</Typography.Link>
                  <Trash onClick={ this.clearUploadData } />
                </Layout.FileName>
              )}
              <Button color="secondary" outline onClick={ this.toggleRootModal }>
                CANCEL
              </Button>
              <Button color="success" onClick={ this.toggleConfirmModal } disabled={ !confirmable }>
                CONFIRM
              </Button>
            </Layout.Actions>
            {file && (
              <React.Fragment>
                <Typography.Status>
                  Successful Imported :{' '}
                  <span style={ { color: '#499b49' } }>{status.success_count || 0}</span>
                </Typography.Status>
                <br />
                <Typography.Status style={ { marginBottom: 16 } }>
                  Failed to Import :{' '}
                  {status.fail_count > 0 ? (
                    <span style={ { color: '#d44b49' } }>
                      {status.fail_count}
                      <Typography.ErrorLink onClick={ this.toggleModal('fail') }>
                        SEE DETAIL
                      </Typography.ErrorLink>
                    </span>
                  ) : (
                    0
                  )}
                </Typography.Status>
                <BaseModal centered isOpen={ modal_open.confirm } toggle={ this.toggleConfirmModal }>
                  <ModalHeader>
                    <Typography.ModalHeader>Confirm importing</Typography.ModalHeader>
                  </ModalHeader>
                  <BaseModalBody>
                    <Label for="exampleText">Remark (Max 500 Character)</Label>
                    <Input type="textarea" value={ this.state.remark } onChange={ this.setRemark } />
                  </BaseModalBody>
                  <BaseModalFooter>
                    <Button color="secondary" outline onClick={ this.toggleConfirmModal }>
                      CANCEL
                    </Button>
                    <Button color="success" onClick={ this.handleImportCost }>
                      IMPORT
                    </Button>
                  </BaseModalFooter>
                </BaseModal>
                <BaseModal centered isOpen={ modal_open.fail } toggle={ this.toggleModal('fail') }>
                  <ModalHeader>
                    <Typography.ModalHeader>Data failed to import</Typography.ModalHeader>
                  </ModalHeader>
                  <BaseModalBody>
                    <ul style={ { listStyle: 'none', padding: 0 } }>
                      {status.error.map(e => (
                        <li key={ shortid.generate() }>{e}</li>
                      ))}
                    </ul>
                  </BaseModalBody>
                </BaseModal>
                <Layout.ScrollWrapper>
                  <Table.Head>
                    <Table.HeadCell>Proj. Code</Table.HeadCell>
                    <Table.HeadCell>Sub Code</Table.HeadCell>
                    <Table.HeadCell>Project Name</Table.HeadCell>
                    <Table.HeadCell>Client</Table.HeadCell>
                    <Table.HeadCell>Code</Table.HeadCell>
                    <Table.HeadCell>Type</Table.HeadCell>
                    <Table.HeadCell>Sale Group</Table.HeadCell>
                    <Table.HeadCell>Date</Table.HeadCell>
                    <Table.HeadCell>Description</Table.HeadCell>
                    <Table.HeadCell>Amount</Table.HeadCell>
                  </Table.Head>
                  <ReactList
                    type="uniform"
                    itemRenderer={ this.renderItem }
                    length={ transactions.length }
                  />
                </Layout.ScrollWrapper>
              </React.Fragment>
            )}
          </ModalBody>
        </Modal>
      </React.Fragment>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  isFetching: ppsSelector.importFetching,
  status: ppsSelector.status,
  transactions: ppsSelector.transactions,
  canImport: ppsSelector.canUseAccountantActions,
})

const mapDispatchToProps = bindActionCreators({
  onFileUpload: ppsAction.importActualCost,
  clear: ppsAction.resetImportActual,
  refresh: ppsAction.fetchProjectsData,
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AccountingMenu)



// WEBPACK FOOTER //
// ./src/pages/project-profit-sheet/containers/ImportListMenu.js